import React, { useEffect, useId, useState } from "react";
import axios from "../axios/AxiosInstance";
import { invoiceMonthlyTotals, invoiceMonthLabel } from "../utils/invoiceMonthlyTotals";
import { apiErrorMessage } from "../utils/apiError";
import "./RevenueChart.css";
import Jimi from "./Jimi";

const euros = value => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(value);

export default function RevenueChart({ invoices: suppliedInvoices, loading = false, error = "", endMonth, onEndMonthChange }) {
  const gradientId = useId().replace(/:/g, "");
  const [remote, setRemote] = useState({ invoices: [], loading: true, error: "" });
  useEffect(() => {
    if (suppliedInvoices !== undefined) return;
    let active = true;
    axios.get("facture/freelance").then(({ data }) => {
      if (!Array.isArray(data)) throw new Error("La réponse des factures n’est pas une liste.");
      if (active) setRemote({ invoices: data, loading: false, error: "" });
    }).catch(cause => {
      if (active) setRemote({ invoices: [], loading: false, error: apiErrorMessage(cause, "Impossible de charger le chiffre d’affaires.") });
    });
    return () => { active = false; };
  }, [suppliedInvoices]);
  const source = suppliedInvoices === undefined ? remote : { invoices: suppliedInvoices, loading, error };
  const current = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Paris", year: "numeric", month: "2-digit" }).formatToParts(new Date());
  const currentMonth = `${current.find(part => part.type === "year").value}-${current.find(part => part.type === "month").value}`;
  const [chosenMonth, setChosenMonth] = useState("");
  const periodEnd = endMonth || chosenMonth || currentMonth;
  const [year, month] = periodEnd.split("-").map(Number);
  const totals = new Map(invoiceMonthlyTotals(source.invoices).map(row => [row.month, row]));
  const months = Array.from({ length: 12 }, (_, index) => {
    const date = new Date(year, month - 12 + index, 1);
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    const row = totals.get(key);
    return { key, value: row ? row.amountHT : 0, incomplete: Boolean(row?.htIncomplete), label: date.toLocaleDateString("fr-FR", { month: "short", year: "2-digit" }) };
  });
  const hasAmounts = months.some(row => row.value !== null && row.value !== 0);
  const incomplete = months.some(row => row.incomplete);
  const total = months.reduce((sum, row) => sum + (row.value ?? 0), 0);
  const max = Math.max(2, ...months.map(row => row.value ?? 0));
  const min = Math.min(0, ...months.map(row => row.value ?? 0));
  const y = value => 205 - ((value - min) / (max - min)) * 170;
  const baseline = y(0);
  return <section className="revenue-chart" aria-label="Chiffre d’affaires HT sur 12 mois">
    <header className="revenue-chart-header">
      <div><span className="revenue-chart-eyebrow">PERFORMANCE</span><h2>Évolution du chiffre d’affaires</h2>
      <p>{invoiceMonthLabel(months[0].key)} – {invoiceMonthLabel(months[11].key)}</p></div>
      <div className="revenue-chart-controls"><span className="revenue-chart-period">12 mois · HT</span>
      {(!endMonth || onEndMonthChange) && <label>Jusqu’à<input type="month" lang="fr-FR" aria-label="Dernier mois du graphique CA HT" value={periodEnd} onChange={event => { if (/^\d{4}-\d{2}$/.test(event.target.value)) (onEndMonthChange || setChosenMonth)(event.target.value); }} /></label>}</div>
    </header>
    {source.loading ? <p role="status">Chargement du chiffre d’affaires…</p> : source.error ? <p role="alert">{source.error}</p> : <>
      <div className="revenue-chart-total"><strong>{euros(total)}</strong><span>{incomplete ? "Total HT renseigné · données incomplètes" : "Chiffre d’affaires HT cumulé"}</span></div>
      {hasAmounts ? <div className="revenue-chart-scroll">
        <svg viewBox="0 0 960 260" role="img" aria-label="Montants mensuels du chiffre d’affaires hors taxes">
          <defs><linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0ea5a4" /><stop offset="100%" stopColor="#99e4dd" /></linearGradient></defs>
          {[min, (max + min) / 2, max].map((tick, index) => <g key={index}>
            <line x1="95" x2="945" y1={y(tick)} y2={y(tick)} stroke="#e2e8f0" strokeDasharray="4 5" />
            <text x="85" y={y(tick) + 5} textAnchor="end">{euros(tick)}</text>
          </g>)}
          <line x1="95" x2="945" y1={baseline} y2={baseline} stroke="#94a3b8" />
          {months.map((row, index) => <g key={row.key}>
            <title>{invoiceMonthLabel(row.key)} : {row.value === null ? "HT non renseigné" : euros(row.value)}{row.incomplete ? " (incomplet)" : ""}</title>
            {row.value !== null && row.value !== 0 && <rect x={110 + index * 70} y={Math.min(y(row.value), baseline)} width="38" height={Math.max(2, Math.abs(y(row.value) - baseline))} rx="4" fill={row.value < 0 ? "#e11d48" : `url(#${gradientId})`} opacity={row.incomplete ? 0.55 : 1} />}
            {row.value !== null && <text
              className="revenue-chart-bar-value"
              x={129 + index * 70}
              y={Math.min(y(row.value), baseline) - 9}
              textAnchor="middle"
              textLength={euros(row.value).length > 9 ? 64 : undefined}
              lengthAdjust="spacingAndGlyphs"
            >{euros(row.value)}{row.incomplete ? "*" : ""}</text>}
            <text x={129 + index * 70} y="238" textAnchor="middle">{row.label}</text>
          </g>)}
        </svg>
      </div> : <div className="revenue-chart-empty">
        <Jimi size="large" decorative={false} />
        <strong>{incomplete ? "Montants HT à compléter" : "On construit la suite ensemble !"}</strong>
        <span>{incomplete ? "Renseignez les montants HT des factures pour afficher la courbe." : "Un appel, une rencontre, une opportunité : chaque action compte. À vous de jouer !"}</span>
        {!incomplete && <span className="revenue-chart-empty-note">Aucun chiffre d’affaires sur cette période</span>}
      </div>}
      {hasAmounts && months[11].value === 0 && <div className="revenue-chart-motivation">
        <Jimi size="medium" decorative={false} />
        <div><strong>On construit la suite ensemble !</strong>
          <p>Un appel, une rencontre, une opportunité : chaque action compte. À vous de jouer !</p>
        </div>
      </div>}
      {months.some(row => row.incomplete) && <p role="status">Certains montants HT sont manquants : les totaux concernés sont incomplets.</p>}
      <p className="revenue-chart-footnote">Source : Factures clients · Date de facturation · Avoirs inclus</p>
    </>}
  </section>;
}
